import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/api";
import { Plus, PencilSimple, Eye, Archive, Trash, X, UploadSimple, MusicNotes, UsersThree } from "@phosphor-icons/react";

const CATEGORIES = [
  { value: "Hypno-Breastfeeding", label: "Hypno-Breastfeeding" },
  { value: "Hypnobirthing Kehamilan", label: "Hypnobirthing Kehamilan" },
  { value: "Hypnobirthing Persiapan Persalinan", label: "Hypnobirthing Persiapan Persalinan" },
  { value: "stress_release", label: "Relaksasi & Stress Release" },
  { value: "hypnofertility", label: "Promil & Hypnofertility" },
];
const catLabel = (c) => (CATEGORIES.find((x) => x.value === c) || {}).label || c;
const EMPTY = { title: "", slug: "", category: CATEGORIES[0].value, description: "", benefits: "", usage_instructions: "", safety_notice: "", price: "", duration_sec: "", preview_duration_sec: "", pregnancy_min_weeks: "", status: "Draft" };
const TONE = { Published: "bg-emerald-100 text-emerald-700", Draft: "bg-slate-100 text-slate-600", Archived: "bg-amber-100 text-amber-700" };
const inC = "w-full rounded-xl border border-pink-100 bg-[#FDFBFE] px-3 py-2 text-sm outline-none focus:border-[#EC4899]";
const hydrate = (o) => { const c = { ...EMPTY, ...o }; for (const k in c) if (c[k] == null) c[k] = ""; return c; };

export default function RelaxAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [access, setAccess] = useState(null); // product whose access is managed
  const [owners, setOwners] = useState([]);
  const [grantEmail, setGrantEmail] = useState("");

  const loadOwners = useCallback(async (pid) => {
    try { setOwners((await api.get(`/relax/entitlements/${pid}`)).data.owners); }
    catch (e) { toast.error(apiError(e)); }
  }, []);
  const openAccess = (p) => { setAccess(p); setGrantEmail(""); setOwners([]); loadOwners(p.id); };
  const doGrant = async () => {
    const email = grantEmail.trim().toLowerCase();
    if (!email) { toast.error("Isi email pasien"); return; }
    try { const r = await api.post(`/relax/grant`, { email, product_ids: [access.id] });
      toast.success(`Akses diberikan (${r.data.granted})`); setGrantEmail(""); loadOwners(access.id); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doRevoke = async (o) => {
    const reason = window.prompt("Alasan pencabutan akses:", "");
    if (reason == null || !reason.trim()) { if (reason != null) toast.error("Alasan wajib diisi"); return; }
    try { await api.post(`/relax/revoke`, { patient_id: o.patient_id, product_id: access.id, reason: reason.trim() });
      toast.success("Akses dicabut"); loadOwners(access.id); }
    catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(() => {
    api.get("/relax/products").then((r) => setItems(r.data.items)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Judul wajib diisi"); return; }
    setBusy(true);
    try {
      const saved = editing.id
        ? (await api.put(`/relax/products/${editing.id}`, editing)).data
        : (await api.post("/relax/products", editing)).data;
      toast.success("Produk disimpan"); setEditing(hydrate(saved)); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const setStatus = async (p, status) => {
    try { await api.put(`/relax/products/${p.id}`, { status }); toast.success(`Status: ${status}`); load(); if (editing?.id === p.id) setEditing({ ...editing, status }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (p) => {
    if (!window.confirm(p.status === "Draft" ? "Hapus draft ini?" : "Arsipkan produk ini?")) return;
    try { await api.delete(`/relax/products/${p.id}`); toast.success("Selesai"); load(); if (editing?.id === p.id) setEditing(null); }
    catch (e) { toast.error(apiError(e)); }
  };
  const upload = async (kind, fileEl) => {
    const f = fileEl.files?.[0]; if (!f || !editing?.id) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/relax/products/${editing.id}/${kind}`, fd); toast.success(`${kind} diunggah`);
      const fresh = (await api.get(`/relax/products/${editing.id}`)).data; setEditing(hydrate({ ...editing, ...fresh })); load(); }
    catch (e) { toast.error(apiError(e)); }
    fileEl.value = "";
  };
  const delFile = async (kind) => {
    try { await api.delete(`/relax/products/${editing.id}/file/${kind}`); const fresh = (await api.get(`/relax/products/${editing.id}`)).data; setEditing(hydrate({ ...editing, ...fresh })); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="relax-admin">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-[#4C1D95]"><MusicNotes size={24} weight="duotone" className="text-[#DB2777]" /> Relaksasi Digital</h1>
        <button data-testid="relax-new" onClick={() => setEditing({ ...EMPTY })} className="flex items-center gap-1.5 rounded-xl bg-[#7C4DE0] px-4 py-2 text-sm font-bold text-white"><Plus size={16} weight="bold" /> Produk Baru</button>
      </div>

      <div className="grid gap-3">
        {items.map((p) => (
          <div key={p.id} data-testid={`relax-row-${p.id}`} className="flex items-center justify-between rounded-2xl border border-pink-100 bg-white px-4 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2"><span className="font-bold text-slate-800">{p.title}</span><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${TONE[p.status]}`}>{p.status}</span></div>
              <div className="text-xs text-slate-500">{catLabel(p.category)} · Rp{(p.price || 0).toLocaleString("id-ID")} · {p.has_audio ? "audio✓" : "audio✗"} {p.has_preview ? "preview✓" : "preview✗"}</div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button data-testid={`relax-edit-${p.id}`} onClick={() => setEditing(hydrate(p))} className="rounded-lg bg-slate-100 p-2 text-slate-600"><PencilSimple size={16} /></button>
              {p.status === "Published" && <button data-testid={`relax-access-${p.id}`} onClick={() => openAccess(p)} className="rounded-lg bg-[#EEE6FF] p-2 text-[#7C4DE0]" title="Kelola Akses Pasien"><UsersThree size={16} /></button>}
              {p.status !== "Published" && <button data-testid={`relax-publish-${p.id}`} onClick={() => setStatus(p, "Published")} className="rounded-lg bg-emerald-100 p-2 text-emerald-700"><Eye size={16} /></button>}
              {p.status === "Published" && <button onClick={() => setStatus(p, "Archived")} className="rounded-lg bg-amber-100 p-2 text-amber-700"><Archive size={16} /></button>}
              <button data-testid={`relax-del-${p.id}`} onClick={() => del(p)} className="rounded-lg bg-rose-100 p-2 text-rose-600"><Trash size={16} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="rounded-2xl border border-dashed border-pink-200 py-8 text-center text-sm text-slate-400">Belum ada produk audio.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div data-testid="relax-editor" onClick={(e) => e.stopPropagation()} className="my-6 w-full max-w-2xl rounded-3xl bg-white p-6">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-lg font-extrabold text-[#4C1D95]">{editing.id ? "Edit" : "Produk Baru"}</h2><button onClick={() => setEditing(null)}><X size={20} /></button></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input data-testid="relax-f-title" placeholder="Judul" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inC} />
              <select data-testid="relax-f-category" value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inC}>{CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</select>
              <input type="number" data-testid="relax-f-price" placeholder="Harga (Rp)" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className={inC} />
              <input type="number" placeholder="Durasi audio (detik)" value={editing.duration_sec} onChange={(e) => setEditing({ ...editing, duration_sec: e.target.value })} className={inC} />
              <input type="number" placeholder="Durasi preview (detik)" value={editing.preview_duration_sec} onChange={(e) => setEditing({ ...editing, preview_duration_sec: e.target.value })} className={inC} />
              <input type="number" data-testid="relax-f-weeks" placeholder="Min UK (minggu, opsional)" value={editing.pregnancy_min_weeks} onChange={(e) => setEditing({ ...editing, pregnancy_min_weeks: e.target.value })} className={inC} />
            </div>
            <textarea placeholder="Deskripsi" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={`${inC} mt-3`} rows={2} />
            <textarea placeholder="Manfaat" value={editing.benefits} onChange={(e) => setEditing({ ...editing, benefits: e.target.value })} className={`${inC} mt-3`} rows={2} />
            <textarea placeholder="Petunjuk penggunaan" value={editing.usage_instructions} onChange={(e) => setEditing({ ...editing, usage_instructions: e.target.value })} className={`${inC} mt-3`} rows={2} />
            <textarea placeholder="Catatan keselamatan" value={editing.safety_notice} onChange={(e) => setEditing({ ...editing, safety_notice: e.target.value })} className={`${inC} mt-3`} rows={2} />

            {editing.id && (
              <div className="mt-4 grid gap-2 rounded-2xl bg-[#FAF7FF] p-3 text-sm">
                <FileRow label="Cover (JPG/PNG/WebP ≤2MB)" has={editing.has_cover} onUp={(el) => upload("cover", el)} onDel={() => delFile("cover")} testid="relax-up-cover" accept="image/*" />
                <FileRow label="Audio penuh (MP3/M4A/AAC ≤100MB)" has={editing.has_audio} onUp={(el) => upload("audio", el)} onDel={() => delFile("audio")} testid="relax-up-audio" accept=".mp3,.m4a,.aac,audio/*" />
                <FileRow label="Preview 20–30 dtk (terpisah)" has={editing.has_preview} onUp={(el) => upload("preview", el)} onDel={() => delFile("preview")} testid="relax-up-preview" accept=".mp3,.m4a,.aac,audio/*" />
              </div>
            )}
            <div className="mt-5 flex items-center justify-end gap-2">
              {editing.id && editing.status !== "Published" && <button data-testid="relax-save-publish" onClick={() => setStatus(editing, "Published")} className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white">Publish</button>}
              <button data-testid="relax-save" onClick={save} disabled={busy} className="rounded-xl bg-[#7C4DE0] px-5 py-2 text-sm font-bold text-white disabled:opacity-60">{busy ? "..." : "Simpan"}</button>
            </div>
          </div>
        </div>
      )}

      {access && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setAccess(null)}>
          <div data-testid="relax-access-modal" onClick={(e) => e.stopPropagation()} className="my-6 w-full max-w-lg rounded-3xl bg-white p-6">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-lg font-extrabold text-[#4C1D95]"><UsersThree size={20} weight="duotone" /> Kelola Akses</h2>
              <button onClick={() => setAccess(null)}><X size={20} /></button>
            </div>
            <p className="mb-4 text-sm text-slate-500">{access.title}</p>

            <div className="rounded-2xl bg-[#FAF7FF] p-3">
              <label className="text-xs font-bold text-slate-600">Beri akses ke email pasien (harus terverifikasi)</label>
              <div className="mt-1.5 flex gap-2">
                <input data-testid="relax-grant-email" type="email" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="email@pasien.com" className={inC} />
                <button data-testid="relax-grant-btn" onClick={doGrant} className="shrink-0 rounded-xl bg-[#7C4DE0] px-4 py-2 text-sm font-bold text-white">Beri Akses</button>
              </div>
            </div>

            <div className="mt-4">
              <div className="mb-2 text-sm font-bold text-slate-700">Pasien dengan akses ({owners.length})</div>
              <div className="grid gap-2">
                {owners.map((o) => (
                  <div key={o.patient_id} data-testid={`relax-owner-${o.patient_id}`} className="flex items-center justify-between rounded-xl border border-pink-100 bg-white px-3 py-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-bold text-slate-800">{o.email || o.patient_id}</div>
                      <div className="text-[11px] text-slate-400">{o.source === "manual_grant" ? "Grant manual" : o.source}</div>
                    </div>
                    {o.source === "manual_grant"
                      ? <button data-testid={`relax-revoke-${o.patient_id}`} onClick={() => doRevoke(o)} className="shrink-0 rounded-lg bg-rose-100 px-3 py-1.5 text-xs font-bold text-rose-600">Cabut</button>
                      : <span className="text-[11px] text-slate-400">—</span>}
                  </div>
                ))}
                {owners.length === 0 && <p className="rounded-xl border border-dashed border-pink-200 py-6 text-center text-sm text-slate-400">Belum ada pasien dengan akses.</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FileRow({ label, has, onUp, onDel, testid, accept }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-slate-600">{label} {has ? <b className="text-emerald-600">✓ ada</b> : <span className="text-slate-400">— belum</span>}</span>
      <span className="flex items-center gap-1">
        <label className="flex cursor-pointer items-center gap-1 rounded-lg bg-[#7C4DE0] px-2.5 py-1.5 text-xs font-bold text-white">
          <UploadSimple size={14} /> Unggah
          <input data-testid={testid} type="file" accept={accept} className="hidden" onChange={(e) => onUp(e.target)} />
        </label>
        {has && <button onClick={onDel} className="rounded-lg bg-rose-100 p-1.5 text-rose-600"><Trash size={14} /></button>}
      </span>
    </div>
  );
}
